import {APP_DATE_FORMAT, type Worker} from "@mytour/shared";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import relativeTime from "dayjs/plugin/relativeTime";
import {Alert, Card, CardBody, CardTitle} from "reactstrap";
import {DeleteWorkerAction} from "./actions/DeleteWorkerAction.js";
import {EditWorkerAction} from "./actions/EditWorkerAction.js";
import {RegenerateWorkerPasswordAction} from "./actions/RegenerateWorkerPasswordAction.js";
import {ResendWorkerInvitationAction} from "./actions/ResendWorkerInvitationAction.js";
import {ToggleWorkerActiveAction} from "./actions/ToggleWorkerActiveAction.js";

dayjs.extend(relativeTime);
dayjs.locale("fr");

interface WorkerCardProps {
  worker: Worker;
}

export function WorkerCard({worker}: WorkerCardProps) {
  const getTimeAgo = (date: Date) => {
    return dayjs(date).fromNow(true);
  };

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <CardTitle tag="h5" className="mb-1">
              {worker.firstName} {worker.lastName}
            </CardTitle>
            <div className="text-muted mb-2">{worker.email}</div>
            <div className="text-muted small">
              {worker.invitationAcceptedAt && (
                <> Depuis le {dayjs(worker.invitationAcceptedAt).format(APP_DATE_FORMAT)}</>
              )}
            </div>
          </div>
          <div>
            {worker.isActive ? (
              <span className="badge bg-success">Actif</span>
            ) : (
              <span className="badge bg-secondary">Inactif</span>
            )}
          </div>
        </div>

        {!worker.invitationAcceptedAt && (
          <Alert
            color="warning"
            className="mb-3 d-flex justify-content-between align-items-center"
          >
            <div>
              <strong>Invitation en attente</strong>
              <div className="small">
                L&apos;intervenant n&apos;a pas encore accepté son invitation (Il y a{" "}
                {getTimeAgo(worker.invitedAt)}).
              </div>
            </div>
            <ResendWorkerInvitationAction worker={worker} />
          </Alert>
        )}

        <div className="mb-3">
          <strong>Adresse:</strong>
          <div className="text-muted">
            {worker.address.streetAddress}
            {worker.address.streetAddress2 && `, ${worker.address.streetAddress2}`}
            <br />
            {worker.address.zipCode} {worker.address.city}
            <br />
            {worker.address.country}
          </div>
        </div>

        <div className="mb-3">
          <strong>Permissions:</strong>
          <div className="d-flex gap-2 mt-1">
            {worker.canCreateInvoice && <span className="badge bg-info">Facturation</span>}
            {worker.canEditCustomer && <span className="badge bg-success">Édition client</span>}
            {!worker.canCreateInvoice && !worker.canEditCustomer && (
              <span className="text-muted">Aucune</span>
            )}
          </div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <EditWorkerAction worker={worker} />
          <ToggleWorkerActiveAction worker={worker} />
          {worker.invitationAcceptedAt && <RegenerateWorkerPasswordAction worker={worker} />}
          <DeleteWorkerAction worker={worker} />
        </div>
      </CardBody>
    </Card>
  );
}
